// services/firestore.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

export class FirestoreService {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.collectionRef = collection(db, collectionName);
  }

  // Create a new document
  async create(data) {
    try {
      const docData = {
        ...data,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };
      const docRef = await addDoc(this.collectionRef, docData);
      return { id: docRef.id, ...docData };
    } catch (error) {
      console.error(`Error creating document in ${this.collectionName}:`, error);
      throw new Error(`Failed to create ${this.collectionName.slice(0, -1)}`);
    }
  }

  // Get a single document by ID
  async getById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Error getting document ${id} from ${this.collectionName}:`, error);
      throw new Error(`Failed to get ${this.collectionName.slice(0, -1)}`);
    }
  }

  // Get all documents
  async getAll() {
    try {
      const querySnapshot = await getDocs(this.collectionRef);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error getting all documents from ${this.collectionName}:`, error);
      throw new Error(`Failed to get ${this.collectionName}`);
    }
  }

  // Update a document
  async update(id, data) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const updateData = {
        ...data,
        updated_at: serverTimestamp()
      };
      await updateDoc(docRef, updateData);
      return { id, ...updateData };
    } catch (error) {
      console.error(`Error updating document ${id} in ${this.collectionName}:`, error);
      throw new Error(`Failed to update ${this.collectionName.slice(0, -1)}`);
    }
  }

  // Delete a document
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(`Error deleting document ${id} from ${this.collectionName}:`, error);
      throw new Error(`Failed to delete ${this.collectionName.slice(0, -1)}`);
    }
  }

  // Query documents with conditions
  async query(conditions = [], orderByField = null, orderDirection = 'asc') {
    try {
      let q = this.collectionRef;

      // Add where conditions
      conditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });

      // Add ordering
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error querying ${this.collectionName}:`, error);
      throw new Error(`Failed to query ${this.collectionName}`);
    }
  }

  // Subscribe to real-time updates
  subscribe(callback, conditions = [], orderByField = null) {
    try {
      let q = this.collectionRef;

      // Add where conditions
      conditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });

      // Add ordering
      if (orderByField) {
        q = query(q, orderBy(orderByField));
      }

      return onSnapshot(q, (querySnapshot) => {
        const documents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(documents);
      }, (error) => {
        console.error(`Error in subscription for ${this.collectionName}:`, error);
        callback(null, error);
      });
    } catch (error) {
      console.error(`Error setting up subscription for ${this.collectionName}:`, error);
      throw new Error(`Failed to subscribe to ${this.collectionName}`);
    }
  }

  // Batch operations
  async batchWrite(operations) {
    try {
      const batch = writeBatch(db);

      operations.forEach(operation => {
        const docRef = doc(db, this.collectionName, operation.id);
        
        switch (operation.type) {
          case 'create':
            batch.set(docRef, {
              ...operation.data,
              created_at: serverTimestamp(),
              updated_at: serverTimestamp()
            });
            break;
          case 'update':
            batch.update(docRef, {
              ...operation.data,
              updated_at: serverTimestamp()
            });
            break;
          case 'delete':
            batch.delete(docRef);
            break;
          default:
            throw new Error(`Unknown operation type: ${operation.type}`);
        }
      });

      await batch.commit();
      return true;
    } catch (error) {
      console.error(`Error in batch operation for ${this.collectionName}:`, error);
      throw new Error(`Failed to execute batch operation`);
    }
  }
}

// Error handling utility
export const handleFirestoreError = (error) => {
  console.error('Firestore error:', error);
  
  switch (error.code) {
    case 'permission-denied':
      return 'You do not have permission to perform this action';
    case 'unavailable':
      return 'Service is currently unavailable. Please try again later';
    case 'cancelled':
      return 'Operation was cancelled';
    case 'deadline-exceeded':
      return 'Operation timed out. Please try again';
    default:
      return error.message || 'An unexpected error occurred';
  }
};